import type { MarginPlanInputs } from "./marginProjection";

export const MARGIN_PLAN_STORAGE_KEY = "smart-margin:margin-planner:v1";

export interface SavedMarginPlan {
  id: string;
  name: string;
  inputs: MarginPlanInputs;
  updatedAt: string;
}

export interface StoredMarginPlans {
  draft: MarginPlanInputs;
  savedPlans: SavedMarginPlan[];
  selectedPlanId: string | null;
}

// Fields present before variable-rate scenarios were introduced. Keeping these separate
// lets existing browser drafts and named plans migrate without being discarded.
const LEGACY_NUMBER_KEYS: (keyof MarginPlanInputs)[] = [
  "currentAge",
  "retirementAge",
  "endAge",
  "startingPortfolio",
  "initialMargin",
  "annualSpending",
  "monthlyContribution",
  "contributionEndAge",
  "expectedReturnPercent",
  "volatilityPercent",
  "marginRatePercent",
  "inflationPercent",
  "maxLtvPercent",
];

export function normalizeMarginPlanInputs(
  value: unknown,
  fallback: MarginPlanInputs,
): MarginPlanInputs | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Record<string, unknown>;
  if (!LEGACY_NUMBER_KEYS.every(
    (key) => typeof candidate[key] === "number" && Number.isFinite(candidate[key]),
  )) return null;

  const numberOr = (key: string, defaultValue: number) => (
    typeof candidate[key] === "number" && Number.isFinite(candidate[key])
      ? candidate[key]
      : defaultValue
  );
  return {
    ...fallback,
    ...(candidate as unknown as MarginPlanInputs),
    retirementAccessAge: numberOr(
      "retirementAccessAge",
      fallback.retirementAccessAge,
    ),
    monthlySocialSecurityBenefit: numberOr(
      "monthlySocialSecurityBenefit",
      fallback.monthlySocialSecurityBenefit,
    ),
    socialSecurityStartAge: numberOr(
      "socialSecurityStartAge",
      fallback.socialSecurityStartAge,
    ),
    socialSecurityColaPercent: numberOr(
      "socialSecurityColaPercent",
      fallback.socialSecurityColaPercent,
    ),
    monthlyPensionBenefit: numberOr(
      "monthlyPensionBenefit",
      fallback.monthlyPensionBenefit,
    ),
    pensionStartAge: numberOr("pensionStartAge", fallback.pensionStartAge),
    pensionColaPercent: numberOr("pensionColaPercent", fallback.pensionColaPercent),
    retirementAssets: numberOr("retirementAssets", fallback.retirementAssets),
    externalAssets: numberOr("externalAssets", fallback.externalAssets),
    externalAssetGrowthPercent: numberOr(
      "externalAssetGrowthPercent",
      fallback.externalAssetGrowthPercent,
    ),
    marginRateMode: candidate.marginRateMode === "range" ? "range" : "fixed",
    marginRateMinPercent: numberOr("marginRateMinPercent", fallback.marginRateMinPercent),
    marginRateMaxPercent: numberOr("marginRateMaxPercent", fallback.marginRateMaxPercent),
    spendingMode: candidate.spendingMode === "portfolio_percent"
      ? "portfolio_percent"
      : "fixed",
    spendingDollarBasis: candidate.spendingDollarBasis === "today"
      ? "today"
      : "retirement_start",
    spendingPercent: numberOr("spendingPercent", fallback.spendingPercent),
    spendingBorrowPercent: numberOr(
      "spendingBorrowPercent",
      fallback.spendingBorrowPercent,
    ),
    dividendYieldPercent: numberOr(
      "dividendYieldPercent",
      fallback.dividendYieldPercent,
    ),
    dividendTaxRatePercent: numberOr(
      "dividendTaxRatePercent",
      fallback.dividendTaxRatePercent,
    ),
    capitalGainsTaxEnabled: candidate.capitalGainsTaxEnabled === true,
    startingTaxableCostBasis: numberOr(
      "startingTaxableCostBasis",
      numberOr("startingPortfolio", fallback.startingTaxableCostBasis),
    ),
    capitalGainsTaxRatePercent: numberOr(
      "capitalGainsTaxRatePercent",
      fallback.capitalGainsTaxRatePercent,
    ),
    dividendUse: candidate.dividendUse === "pay_margin" ? "pay_margin" : "spending",
    flexibleSpendingEnabled: candidate.flexibleSpendingEnabled === true,
    downMarketTriggerPercent: numberOr(
      "downMarketTriggerPercent",
      fallback.downMarketTriggerPercent,
    ),
    spendingCutWithdrawalRatePercent: numberOr(
      "spendingCutWithdrawalRatePercent",
      fallback.spendingCutWithdrawalRatePercent,
    ),
    downMarketSpendingReductionPercent: numberOr(
      "downMarketSpendingReductionPercent",
      fallback.downMarketSpendingReductionPercent,
    ),
    minimumSpendingPercent: numberOr(
      "minimumSpendingPercent",
      fallback.minimumSpendingPercent,
    ),
    contributionStrategy: candidate.contributionStrategy === "pay_margin_first"
      ? "pay_margin_first"
      : "invest",
    contributionMode: candidate.contributionMode === "portfolio_percent"
      ? "portfolio_percent"
      : "fixed",
    contributionPercent: numberOr("contributionPercent", fallback.contributionPercent),
    cashFlowMode: candidate.cashFlowMode === "income_expenses"
      ? "income_expenses"
      : "manual_contributions",
    annualWorkingIncome: numberOr("annualWorkingIncome", fallback.annualWorkingIncome),
    incomeGrowthPercent: numberOr("incomeGrowthPercent", fallback.incomeGrowthPercent),
    annualWorkingExpenses: numberOr("annualWorkingExpenses", fallback.annualWorkingExpenses),
    workingExpenseInflationPercent: numberOr(
      "workingExpenseInflationPercent",
      fallback.workingExpenseInflationPercent,
    ),
    retirementIncomeMode: candidate.retirementIncomeMode === "coast" ? "coast" : "none",
    annualCoastIncome: numberOr("annualCoastIncome", fallback.annualCoastIncome),
    coastIncomeGrowthPercent: numberOr(
      "coastIncomeGrowthPercent",
      fallback.coastIncomeGrowthPercent,
    ),
    coastIncomeEndAge: numberOr("coastIncomeEndAge", fallback.coastIncomeEndAge),
    pauseBorrowingLtvPercent: numberOr(
      "pauseBorrowingLtvPercent",
      fallback.pauseBorrowingLtvPercent,
    ),
    deleveragingLtvPercent: numberOr(
      "deleveragingLtvPercent",
      fallback.deleveragingLtvPercent,
    ),
    targetLtvPercent: numberOr("targetLtvPercent", fallback.targetLtvPercent),
    maintenanceShockEnabled: candidate.maintenanceShockEnabled === true,
    maintenanceShockTimingMode: candidate.maintenanceShockTimingMode === "random_range"
      ? "random_range"
      : "fixed",
    maintenanceShockAge: numberOr("maintenanceShockAge", fallback.maintenanceShockAge),
    maintenanceShockLatestAge: numberOr(
      "maintenanceShockLatestAge",
      fallback.maintenanceShockLatestAge,
    ),
    maintenanceShockDurationYears: numberOr(
      "maintenanceShockDurationYears",
      fallback.maintenanceShockDurationYears,
    ),
    stressedMaxLtvPercent: numberOr(
      "stressedMaxLtvPercent",
      fallback.stressedMaxLtvPercent,
    ),
  };
}

export function loadMarginPlans(fallback: MarginPlanInputs): StoredMarginPlans {
  if (typeof window === "undefined") {
    return { draft: fallback, savedPlans: [], selectedPlanId: null };
  }

  try {
    const raw = window.localStorage.getItem(MARGIN_PLAN_STORAGE_KEY);
    if (!raw) return { draft: fallback, savedPlans: [], selectedPlanId: null };
    const parsed = JSON.parse(raw) as {
      draft?: unknown;
      savedPlans?: unknown;
      selectedPlanId?: unknown;
    };
    const savedPlans = Array.isArray(parsed.savedPlans)
      ? parsed.savedPlans.flatMap((plan): SavedMarginPlan[] => {
        if (!plan || typeof plan !== "object") return [];
        const candidate = plan as Record<string, unknown>;
        const normalized = normalizeMarginPlanInputs(candidate.inputs, fallback);
        return typeof candidate.id === "string"
          && typeof candidate.name === "string"
          && candidate.name.trim().length > 0
          && typeof candidate.updatedAt === "string"
          && normalized
          ? [{
              id: candidate.id,
              name: candidate.name,
              updatedAt: candidate.updatedAt,
              inputs: normalized,
            }]
          : [];
      })
      : [];
    const draft = normalizeMarginPlanInputs(parsed.draft, fallback);
    return {
      draft: draft ?? fallback,
      savedPlans,
      selectedPlanId: typeof parsed.selectedPlanId === "string"
        ? parsed.selectedPlanId
        : null,
    };
  } catch {
    return { draft: fallback, savedPlans: [], selectedPlanId: null };
  }
}

/** Persist the continuously edited draft without making named plans browser-owned. */
export function storeMarginPlanDraft(draft: MarginPlanInputs): boolean {
  if (typeof window === "undefined") return false;
  try {
    const raw = window.localStorage.getItem(MARGIN_PLAN_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) as {
      savedPlans?: unknown;
      selectedPlanId?: unknown;
    } : {};
    const savedPlans = Array.isArray(parsed.savedPlans) ? parsed.savedPlans : [];
    window.localStorage.setItem(
      MARGIN_PLAN_STORAGE_KEY,
      JSON.stringify({
        draft,
        savedPlans,
        selectedPlanId: typeof parsed.selectedPlanId === "string"
          ? parsed.selectedPlanId
          : undefined,
      }),
    );
    return true;
  } catch {
    return false;
  }
}

/** Remember which named plan was selected without changing the continuously edited draft. */
export function storeSelectedMarginPlan(selectedPlanId: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    const raw = window.localStorage.getItem(MARGIN_PLAN_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) as {
      draft?: unknown;
      savedPlans?: unknown;
    } : {};
    window.localStorage.setItem(
      MARGIN_PLAN_STORAGE_KEY,
      JSON.stringify({
        draft: parsed.draft,
        savedPlans: Array.isArray(parsed.savedPlans) ? parsed.savedPlans : [],
        selectedPlanId,
      }),
    );
    return true;
  } catch {
    return false;
  }
}

/** Remove named plans that are confirmed present in DynamoDB after migration. */
export function removeLocalSavedMarginPlans(planIds: string[]): boolean {
  if (typeof window === "undefined") return false;
  try {
    const raw = window.localStorage.getItem(MARGIN_PLAN_STORAGE_KEY);
    if (!raw) return true;
    const parsed = JSON.parse(raw) as {
      draft?: unknown;
      savedPlans?: unknown;
      selectedPlanId?: unknown;
    };
    const migrated = new Set(planIds);
    const savedPlans = Array.isArray(parsed.savedPlans)
      ? parsed.savedPlans.filter((plan) => (
          !plan
          || typeof plan !== "object"
          || typeof (plan as { id?: unknown }).id !== "string"
          || !migrated.has((plan as { id: string }).id)
        ))
      : [];
    window.localStorage.setItem(
      MARGIN_PLAN_STORAGE_KEY,
      JSON.stringify({
        draft: parsed.draft,
        savedPlans,
        selectedPlanId: parsed.selectedPlanId,
      }),
    );
    return true;
  } catch {
    return false;
  }
}
